import { useCurrentElement } from '@vueuse/core';
import { onMounted } from 'vue';

/**
 * Dev-only guard: a skeleton is aria-hidden, so the loading state has to reach
 * screen readers another way. The check passes when the skeleton sits under a
 * role="status" / aria-hidden="true" / role="presentation" ancestor; otherwise
 * it throws on mount. A decorative skeleton opts out by simply not calling this
 * (e.g. `if (!props.decorative) useSkeletonA11yCheck(name)`). No-op in production.
 */
export function useSkeletonA11yCheck(name: string): void {
  if (!import.meta.env.DEV) return;

  const el = useCurrentElement<HTMLElement>();

  onMounted(() => {
    let node = el.value?.parentElement ?? null;
    while (node) {
      if (node.getAttribute('role') === 'status') return;
      if (node.getAttribute('aria-hidden') === 'true') return;
      if (node.getAttribute('role') === 'presentation') return;
      node = node.parentElement;
    }
    throw new Error(
      `[${name}] No accessible loading context found.\n` +
        'This skeleton is aria-hidden, so the loading state must reach screen ' +
        'readers another way:\n' +
        '  - wrap it in a role="status" region if the load should be announced ' +
        '(e.g. a list or section loading), or\n' +
        '  - place it inside an aria-hidden="true" / role="presentation" ancestor ' +
        'if it belongs to an already-hidden region or table skeleton row, or\n' +
        "  - set the shape's opt-out prop (e.g. `decorative`) if it's a standalone " +
        'decorative placeholder announced elsewhere.',
    );
  });
}
